import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReportService } from '../../../services/report.service';
import { FNFResponse } from '../../../models/response';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { RestaurantService } from '../../../services/restaurant.service';
import { Router } from '@angular/router';

/**
 * Newest first by generatedDate. report-service returns rows in DynamoDB
 * order (a random reportId), so the list has to be sorted here. Full
 * timestamps are compared, so same-day reports still order by time; rows
 * whose date can't be read go last. The input array is left untouched.
 */
export function sortReportsNewestFirst(reports: any[]): any[] {
  const time = (report: any) => {
    const parsed = Date.parse(report?.generatedDate);
    return Number.isNaN(parsed) ? -Infinity : parsed;
  };
  return [...reports].sort((a, b) => {
    const diff = time(b) - time(a);
    // Two unreadable dates: -Infinity - -Infinity is NaN, so keep their order.
    return Number.isNaN(diff) ? 0 : diff;
  });
}

@Component({
  selector: 'app-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './report.component.html',
  styleUrl: './report.component.css',
})
export class ReportComponent {
  reportService = inject(ReportService);
  restaurantService = inject(RestaurantService);
  router = inject(Router);
  toastr = inject(ToastrService);
  displayLoading: boolean = false;
  reports: any[] = [];
  unFilteredReports: any[] = [];
  downloadUrl: string = '';
  searchQuery: string = '';
  restaurantPosProvider: string = 'fnf';
  ngOnInit() {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
      if (this.restaurantPosProvider === 'clover') {
        this.router.navigate(['/']);
      }
    });
    this.getReports();
  }

  getReports() {
    this.displayLoading = true;
    this.reportService.getReports().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.unFilteredReports = sortReportsNewestFirst(response.data);
        this.reports = this.unFilteredReports;
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getPreSignedUrl(report: any, type: 'view' | 'download') {
    this.reportService.getPreSignedUrl(report.reportId, type).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          return;
        }
        this.downloadUrl = response.data;
        window.open(this.downloadUrl, '_blank');
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  filterReports() {
    this.reports = this.unFilteredReports.filter((report) =>
      report.reportName.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }
}
